import React, {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, SubmitHandler, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TerminalCall } from "../../types/TerminalCall";
import dayjs from "dayjs";

import { hideModal, hideChildModal, showChildModal } from "../../data/modal";
import { useVoyageTerminalCalls } from "../hooks/useVoyageTerminalCalls";
import { useRefreshVoyageData } from "../hooks/useRefreshVoyageData";

import {
  addContainerLoadingTerminalCallFilterValue,
  addContainerUnloadingTerminalCallFilterValue,
} from "../../data/filter";

import {
  selectedFilteredVoyage,
  selectFilteredTerminalCallsByContainerLoadingTerminal,
  selectFilteredTerminalCallsByContainerUnloadingTerminal,
} from "../../selectors/filterSelector";

import {
  Alert,
  Box,
  Grid,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Paper,
  Stack,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
} from "@mui/material";

import {
  Close as CloseIcon,
  AddLink as AddLinkIcon,
  ArrowRightAlt as ArrowRightAltIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  ScaleOutlined as ScaleOutlinedIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";

import { getFilteredContainers } from "../../data/filteredcontainers";
import { getVessels } from "../../data/vessels";

interface Props {
  containerIds: string[];
  containerUploadId?: number | string | null;
  voyage?: any;
  callback?: (result?: any) => void | Promise<void>;
}

interface FormValues {
  loadingCalls: Record<string, string>;
  unloadingCalls: Record<string, string>;
}

const SMALL_TEXT = {
  fontSize: "0.74rem",
};

const SMALL_BOLD_TEXT = {
  fontSize: "0.74rem",
  fontWeight: 800,
};

const normalizeCollection = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.["hydra:member"])) return value["hydra:member"];
  if (Array.isArray(value?.member)) return value.member;
  if (Array.isArray(value?.items)) return value.items;

  return [];
};

const getTerminalId = (terminal: any) => terminal?.["@id"] || null;

const getVesselApiId = (value: any) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (value["@id"]) return value["@id"];
  if (value.id) return `/api/vessels/${value.id}`;

  return null;
};

const getCallId = (call: any) => {
  if (!call) return "";
  if (typeof call === "string") return call;

  return call["@id"] || "";
};

const getCallShortCode = (call: any) => {
  const iri = getCallId(call);
  const id = iri ? iri.split("/").filter(Boolean).pop() : "";

  return call?.name || call?.reference || call?.code || (id ? `TC-${id}` : "—");
};

const getContainerApiId = (container: any) => {
  if (!container) return "";

  if (typeof container === "string") {
    return container.includes("/") ? container : `/api/containers/${container}`;
  }

  return container["@id"] || (container.id ? `/api/containers/${container.id}` : "");
};

const getContainerWeight = (container: any) => {
  const rawWeight =
    container?.weightInTonnes ??
    container?.weight ??
    container?.grossWeight ??
    container?.weightInTons ??
    0;

  const parsedWeight = Number(rawWeight);

  return Number.isFinite(parsedWeight) ? parsedWeight : 0;
};

const formatWeight = (weight: number) => `${weight.toFixed(3)} T`;

const getContainerTypeLabel = (container: any) => {
  return (
    container?.containerType?.name ||
    container?.containerType?.abbreviation ||
    container?.containerType?.code ||
    "—"
  );
};

const getPlannedDate = (call?: TerminalCall) => {
  if (!call?.planned) return null;

  const planned = dayjs(call.planned);

  return planned.isValid() ? planned : null;
};

const getPlannedDatePlusOneDayForForm = (call?: TerminalCall) => {
  const planned = getPlannedDate(call);

  if (!planned) {
    return null;
  }

  return planned.add(1, "day").format("YYYY-MM-DDTHH:mm");
};

const getSelectedCallWarning = (
  loadingCall?: TerminalCall,
  unloadingCall?: TerminalCall,
  t?: any
) => {
  if (!loadingCall || !unloadingCall) return null;

  if (
    loadingCall["@id"] &&
    unloadingCall["@id"] &&
    loadingCall["@id"] === unloadingCall["@id"]
  ) {
    return t
      ? t("unloadingCallCannotBeSameAsLoadingCall")
      : "The unloading terminal call cannot be the same as the loading terminal call.";
  }

  const loadingPlanned = getPlannedDate(loadingCall);
  const unloadingPlanned = getPlannedDate(unloadingCall);

  if (!loadingPlanned || !unloadingPlanned) return null;

  if (!unloadingPlanned.isAfter(loadingPlanned)) {
    const loadingDate = loadingPlanned.format("DD/MM/YYYY HH:mm");
    const unloadingDate = unloadingPlanned.format("DD/MM/YYYY HH:mm");

    return t
      ? t("unloadingCallMustBeAfterLoadingCall", {
          loading: loadingDate,
          unloading: unloadingDate,
        })
      : `The unloading call must be after the loading call. Loading is ${loadingDate}, unloading is ${unloadingDate}.`;
  }

  return null;
};

const ContainerTerminalCallForm: React.FC<Props> = ({
  containerIds,
  containerUploadId,
  voyage: voyageFromProps,
  callback,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { voyage: voyageFromFilter } = useSelector(selectedFilteredVoyage);
  const voyage = voyageFromProps || voyageFromFilter;

  const modalChildModal = useSelector((state: any) => state.modal?.childModal);
  const filter = useSelector((state: any) => state.filter);
  const vesselsState = useSelector((state: any) => state.vessels?.data);

  const refreshVoyageData = useRefreshVoyageData();

  const hasSubmittedRef = useRef(false);
  const cleanupDoneRef = useRef(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const vessels = useMemo(
    () => normalizeCollection(vesselsState),
    [vesselsState]
  );

  useEffect(() => {
    dispatch(getFilteredContainers(containerIds) as any);
  }, [dispatch, containerIds]);

  useEffect(() => {
    dispatch(getVessels() as any);
  }, [dispatch]);

  useVoyageTerminalCalls(voyage || null);

  const openChildModal = useCallback(
    (opts: any) => dispatch(showChildModal(opts) as any),
    [dispatch]
  );

  const selectedContainersState = useSelector(
    (state: any) => state.filteredContainers || {}
  );

  const containersLoading: boolean = selectedContainersState.loading ?? false;
  const allContainersData: any[] = selectedContainersState.data ?? [];

  const terminalCallsSlice = useSelector(
    (state: any) => state.selectedTerminalCalls || {}
  );

  const terminalCallsLoading: boolean = terminalCallsSlice.loading ?? false;

  const loadingCalls = useSelector(
    selectFilteredTerminalCallsByContainerLoadingTerminal
  );

  const unloadingCalls = useSelector(
    selectFilteredTerminalCallsByContainerUnloadingTerminal
  );

  const filteredContainerData: any[] = useMemo(() => {
    if (!containerIds?.length) return allContainersData;

    const normalizedIds = containerIds.map((id) =>
      typeof id === "string" && id.includes("/")
        ? id.split("/").pop()!
        : String(id)
    );

    return allContainersData.filter((container) => {
      const id = container["@id"]?.split("/").pop();
      return normalizedIds.includes(id);
    });
  }, [allContainersData, containerIds]);

  const loadingTerminalIds = useMemo(
    () =>
      Array.from(
        new Set(
          filteredContainerData
            .map((container: any) => getTerminalId(container.loadingTerminal))
            .filter((id): id is string => !!id)
        )
      ).sort(),
    [filteredContainerData]
  );

  const unloadingTerminalIds = useMemo(
    () =>
      Array.from(
        new Set(
          filteredContainerData
            .map((container: any) => getTerminalId(container.unloadingTerminal))
            .filter((id): id is string => !!id)
        )
      ).sort(),
    [filteredContainerData]
  );

  const routeGroups = useMemo(() => {
    const map: Record<string, any> = {};

    filteredContainerData.forEach((container: any) => {
      const loadingTerminalId: string | null = getTerminalId(
        container.loadingTerminal
      );
      const unloadingTerminalId: string | null = getTerminalId(
        container.unloadingTerminal
      );

      const key = `${loadingTerminalId || "null"}->${
        unloadingTerminalId || "null"
      }`;

      if (!map[key]) {
        map[key] = {
          key,
          loadingTerminalId,
          unloadingTerminalId,
          loadingName: container.loadingTerminal?.name || t("unknown"),
          unloadingName: container.unloadingTerminal?.name || t("unknown"),
          containers: [] as any[],
          totalWeight: 0,
        };
      }

      map[key].containers.push(container);
      map[key].totalWeight += getContainerWeight(container);
    });

    return Object.values(map);
  }, [filteredContainerData, t]);

  const defaultVessel = useMemo(() => {
    const filterVesselId = getVesselApiId(filter?.vessel);

    if (filterVesselId) {
      const vesselFromOptions = vessels.find(
        (vesselItem: any) => getVesselApiId(vesselItem) === filterVesselId
      );

      return vesselFromOptions ?? filter.vessel;
    }

    const routeVessel =
      routeGroups?.[0]?.containers?.[0]?.vessel ??
      routeGroups?.[0]?.containers?.[0]?.loadingTerminalCall?.vessel ??
      routeGroups?.[0]?.containers?.[0]?.unloadingTerminalCall?.vessel ??
      null;

    const voyageVessel =
      voyage?.vessel ??
      voyage?.vessels?.[0] ??
      voyage?.terminalCalls?.[0]?.vessel ??
      null;

    return routeVessel ?? voyageVessel ?? vessels[0] ?? null;
  }, [filter?.vessel, vessels, voyage, routeGroups]);

  const totalContainers = filteredContainerData.length;

  const totalWeight = useMemo(
    () =>
      filteredContainerData.reduce(
        (sum, container) => sum + getContainerWeight(container),
        0
      ),
    [filteredContainerData]
  );

  const prevLoadingKey = useRef<string>("");

  useEffect(() => {
    const key = loadingTerminalIds.join(",");

    if (key && key !== prevLoadingKey.current) {
      dispatch(addContainerLoadingTerminalCallFilterValue(loadingTerminalIds));
      prevLoadingKey.current = key;
    }
  }, [dispatch, loadingTerminalIds]);

  const prevUnloadingKey = useRef<string>("");

  useEffect(() => {
    const key = unloadingTerminalIds.join(",");

    if (key && key !== prevUnloadingKey.current) {
      dispatch(addContainerUnloadingTerminalCallFilterValue(unloadingTerminalIds));
      prevUnloadingKey.current = key;
    }
  }, [dispatch, unloadingTerminalIds]);

  const {
    control,
    handleSubmit,
    reset,
    register,
    getValues,
    formState: { isSubmitting },
    setValue,
  } = useForm<FormValues>({
    defaultValues: { loadingCalls: {}, unloadingCalls: {} },
    shouldUnregister: false,
  });

  const watchedLoading =
    useWatch({
      control,
      name: "loadingCalls",
      defaultValue: {},
    }) || {};

  const watchedUnloading =
    useWatch({
      control,
      name: "unloadingCalls",
      defaultValue: {},
    }) || {};

  useEffect(() => {
    routeGroups.forEach((route) => {
      register(`loadingCalls.${route.key}` as any);
      register(`unloadingCalls.${route.key}` as any);
    });
  }, [register, routeGroups]);

  const getMatchesForTerminal = useCallback(
    (groups: any[], terminalId: string | null) => {
      if (!terminalId) return [];

      const matchGroup = groups.find((group: any) => group.filter === terminalId);

      return (matchGroup?.matches || []) as TerminalCall[];
    },
    []
  );

  const matchKey = useMemo(
    () =>
      [...loadingCalls, ...unloadingCalls]
        .map((group) => {
          const matches = Array.isArray(group.matches) ? group.matches : [];

          return `${group.filter}:${matches.map(getCallId).join(",")}`;
        })
        .sort()
        .join("|"),
    [loadingCalls, unloadingCalls]
  );

  useEffect(() => {
    const currentValues = getValues();

    const defaults: FormValues = {
      loadingCalls: { ...(currentValues.loadingCalls || {}) },
      unloadingCalls: { ...(currentValues.unloadingCalls || {}) },
    };

    routeGroups.forEach((route) => {
      const loadingMatches = getMatchesForTerminal(
        loadingCalls,
        route.loadingTerminalId
      );

      const unloadingMatches = getMatchesForTerminal(
        unloadingCalls,
        route.unloadingTerminalId
      );

      const currentLoadingValue = defaults.loadingCalls[route.key];
      const currentUnloadingValue = defaults.unloadingCalls[route.key];

      if (
        !loadingMatches.some((match) => getCallId(match) === currentLoadingValue)
      ) {
        if (loadingMatches.length === 1) {
          const matchId = getCallId(loadingMatches[0]);

          if (matchId) {
            defaults.loadingCalls[route.key] = matchId;
          }
        } else {
          delete defaults.loadingCalls[route.key];
        }
      }

      if (
        !unloadingMatches.some(
          (match) => getCallId(match) === currentUnloadingValue
        )
      ) {
        if (unloadingMatches.length === 1) {
          const matchId = getCallId(unloadingMatches[0]);

          if (matchId) {
            defaults.unloadingCalls[route.key] = matchId;
          }
        } else {
          delete defaults.unloadingCalls[route.key];
        }
      }
    });

    reset(defaults);
  }, [
    matchKey,
    routeGroups,
    reset,
    getValues,
    loadingCalls,
    unloadingCalls,
    getMatchesForTerminal,
  ]);

  const groupedByRoute = useMemo(() => {
    return routeGroups.map((route) => ({
      ...route,
      previewContainers: route.containers.slice(0, 10),
      remainingCount: Math.max(route.containers.length - 10, 0),
    }));
  }, [routeGroups]);

  const findCallById = useCallback((calls: TerminalCall[], callId?: string) => {
    if (!callId) return undefined;

    return calls.find((call) => call["@id"] === callId);
  }, []);

  const findCallByIdMemo = findCallById;

  const getDefaultPlannedForNewCall = useCallback(
    (mode: "loading" | "unloading", routeKey: string | null) => {
      if (mode !== "unloading" || !routeKey) {
        return null;
      }

      const route = routeGroups.find((routeItem: any) => routeItem.key === routeKey);

      if (!route) {
        return null;
      }

      const loadingCallId =
        getValues(`loadingCalls.${routeKey}` as any) ||
        watchedLoading?.[routeKey] ||
        "";

      if (!loadingCallId) {
        return null;
      }

      const loadingCandidates = getMatchesForTerminal(
        loadingCalls,
        route.loadingTerminalId
      );

      const selectedLoadingCall = findCallByIdMemo(
        loadingCandidates,
        loadingCallId
      );

      return getPlannedDatePlusOneDayForForm(selectedLoadingCall);
    },
    [
      routeGroups,
      getValues,
      watchedLoading,
      getMatchesForTerminal,
      loadingCalls,
      findCallByIdMemo,
    ]
  );

  const allRowsHaveSelectedTerminalCalls = useMemo(() => {
    if (!routeGroups.length) {
      return false;
    }

    return routeGroups.every((route) => {
      const loadingCallId = watchedLoading[route.key] || "";
      const unloadingCallId = watchedUnloading[route.key] || "";

      return Boolean(loadingCallId && unloadingCallId);
    });
  }, [routeGroups, watchedLoading, watchedUnloading]);

  const linkAllDisabled =
    isSubmitting ||
    isCancelling ||
    containersLoading ||
    terminalCallsLoading ||
    !allRowsHaveSelectedTerminalCalls;

  const closeWithoutCleanup = useCallback(() => {
    modalChildModal
      ? dispatch(hideChildModal() as any)
      : dispatch(hideModal() as any);
  }, [dispatch, modalChildModal]);

  const refreshParentPage = useCallback(
    async (result?: any) => {
      if (callback) {
        await callback(result);
      }
    },
    [callback]
  );

  const cleanupContainerUpload = useCallback(
    async (closeAfterCleanup = true) => {
      if (hasSubmittedRef.current || cleanupDoneRef.current) {
        await refreshParentPage();

        if (closeAfterCleanup) {
          closeWithoutCleanup();
        }

        return;
      }

      cleanupDoneRef.current = true;
      setIsCancelling(true);

      try {
        let result: any = null;

        if (containerUploadId) {
          const response = await fetch("/api/container-upload/cancel", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({
              containerUploadId,
            }),
          });

          const responseText = await response.text();

          if (!response.ok) {
            throw new Error(responseText || t("failedToCancelContainerUpload"));
          }

          result = responseText ? JSON.parse(responseText) : null;
        }

        if (callback) {
          await callback({
            ...(result || {}),
            containerUploadId,
          });
        }

        if (voyage) {
          refreshVoyageData(voyage);
        }
      } catch (error) {
        console.error("Failed to cleanup container upload", error);
      } finally {
        setIsCancelling(false);

        if (closeAfterCleanup) {
          closeWithoutCleanup();
        }
      }
    },
    [
      callback,
      closeWithoutCleanup,
      containerUploadId,
      refreshParentPage,
      refreshVoyageData,
      voyage,
      t,
    ]
  );

  useEffect(() => {
    return () => {
      if (
        hasSubmittedRef.current ||
        cleanupDoneRef.current ||
        !containerUploadId
      ) {
        return;
      }

      cleanupDoneRef.current = true;

      fetch("/api/container-upload/cancel", {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          containerUploadId,
        }),
      }).catch((error) => {
        console.error("Failed to cleanup container upload on unmount", error);
      });
    };
  }, [containerUploadId]);

  const close = useCallback(() => {
    cleanupContainerUpload(true);
  }, [cleanupContainerUpload]);

  const handleCreatedLoadingCall = useCallback(
    (routeKey: string | null) => (createdCall: any) => {
      const createdCallId = getCallId(createdCall);

      if (routeKey && createdCallId) {
        setValue(`loadingCalls.${routeKey}` as any, createdCallId, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    },
    [setValue]
  );

  const handleCreatedUnloadingCall = useCallback(
    (routeKey: string | null) => (createdCall: any) => {
      const createdCallId = getCallId(createdCall);

      if (routeKey && createdCallId) {
        setValue(`unloadingCalls.${routeKey}` as any, createdCallId, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    },
    [setValue]
  );

  const openCreateCallModal = useCallback(
    ({
      terminal,
      terminalName,
      mode,
      routeKey,
    }: {
      terminal: any;
      terminalName: string;
      mode: "loading" | "unloading";
      routeKey: string | null;
    }) => {
      const defaultPlanned = getDefaultPlannedForNewCall(mode, routeKey);

      const terminalCallDefaults = defaultPlanned
        ? {
            planned: defaultPlanned,
          }
        : {};

      openChildModal({
        title: t("addPlanningToTerminal", { terminal: terminalName }),
        description: t("createPlanning"),
        form: "simpleterminalcall",
        formProps: {
          callback:
            mode === "loading"
              ? handleCreatedLoadingCall(routeKey)
              : handleCreatedUnloadingCall(routeKey),
          showVesselField: true,
          terminal,
          vessel: defaultVessel,
          vessels,
          voyage,
          voyages: voyage ? [voyage] : [],
          planned: defaultPlanned,
          defaultPlanned,
          initialPlanned: defaultPlanned,
          terminalCallDefaults,
          initialTerminalCall: terminalCallDefaults,
        },
      });
    },
    [
      openChildModal,
      defaultVessel,
      vessels,
      voyage,
      handleCreatedLoadingCall,
      handleCreatedUnloadingCall,
      getDefaultPlannedForNewCall,
      t,
    ]
  );

  const selectTerminalCall = useCallback(
    (fieldName: any, callId: string) => {
      setValue(fieldName, callId, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const renderCallOption = useCallback(
    ({
      call,
      checked,
      fieldName,
      invalid,
      invalidLabel,
    }: {
      call: TerminalCall;
      checked: boolean;
      fieldName: any;
      invalid?: boolean;
      invalidLabel?: string;
    }) => {
      const callId = getCallId(call);
      const planned = getPlannedDate(call);

      return (
        <Paper
          key={callId}
          variant="outlined"
          role="radio"
          aria-checked={checked}
          tabIndex={0}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            selectTerminalCall(fieldName, callId);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              selectTerminalCall(fieldName, callId);
            }
          }}
          sx={{
            p: 0.85,
            borderRadius: 1.5,
            cursor: "pointer",
            outline: "none",
            borderColor: checked
              ? invalid
                ? "#ef4444"
                : "#1976d2"
              : invalid
              ? "rgba(239, 68, 68, 0.32)"
              : "rgba(15, 23, 42, 0.14)",
            backgroundColor: checked
              ? invalid
                ? "rgba(239, 68, 68, 0.08)"
                : "rgba(25, 118, 210, 0.08)"
              : invalid
              ? "rgba(239, 68, 68, 0.035)"
              : "#fff",
            transition: "all 120ms ease",
            "&:hover": {
              borderColor: invalid ? "#ef4444" : "#1976d2",
              backgroundColor: invalid
                ? "rgba(239, 68, 68, 0.08)"
                : "rgba(25, 118, 210, 0.06)",
            },
            "&:focus-visible": {
              borderColor: invalid ? "#ef4444" : "#1976d2",
              boxShadow: invalid
                ? "0 0 0 2px rgba(239, 68, 68, 0.16)"
                : "0 0 0 2px rgba(25, 118, 210, 0.16)",
            },
          }}
        >
          <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <Radio
              checked={checked}
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
              size="small"
              onChange={() => selectTerminalCall(fieldName, callId)}
              onClick={(event) => event.stopPropagation()}
              sx={{
                p: 0,
                mt: 0.15,
                color: invalid ? "#ef4444" : "#94a3b8",
                "&.Mui-checked": {
                  color: invalid ? "#ef4444" : "#1976d2",
                },
              }}
            />

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                <Typography
                  noWrap
                  sx={{
                    fontSize: "0.76rem",
                    fontWeight: 900,
                    color: invalid ? "#b91c1c" : "text.primary",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {getCallShortCode(call)}
                </Typography>

                {checked ? (
                  <Chip
                    size="small"
                    label={t("selected")}
                    sx={{
                      height: 19,
                      fontSize: "0.62rem",
                      fontWeight: 900,
                      backgroundColor: invalid
                        ? "rgba(239, 68, 68, 0.12)"
                        : "rgba(25, 118, 210, 0.12)",
                      color: invalid ? "#b91c1c" : "#1976d2",
                    }}
                  />
                ) : null}
              </Stack>

              <Typography
                noWrap
                sx={{
                  mt: 0.25,
                  fontSize: "0.68rem",
                  color: invalid ? "#b91c1c" : "text.secondary",
                  fontWeight: 900,
                }}
              >
                {planned
                  ? planned.format("DD MMM YYYY HH:mm")
                  : t("noPlannedDate")}
              </Typography>

              {call.terminal?.name ? (
                <Typography
                  noWrap
                  sx={{
                    mt: 0.15,
                    fontSize: "0.66rem",
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                >
                  {call.terminal.name}
                </Typography>
              ) : null}

              {invalid && invalidLabel ? (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ mt: 0.5 }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 14, color: "#ef4444" }} />

                  <Typography
                    sx={{
                      fontSize: "0.66rem",
                      color: "#b91c1c",
                      fontWeight: 800,
                    }}
                  >
                    {invalidLabel}
                  </Typography>
                </Stack>
              ) : null}
            </Box>
          </Stack>
        </Paper>
      );
    },
    [selectTerminalCall, t]
  );

  const renderCallPicker = useCallback(
    ({
      type,
      routeKey,
      terminalId,
      terminalName,
      selectedCallId,
      candidates,
      terminal,
      invalidCallIds,
      rowWarning,
    }: {
      type: "loading" | "unloading";
      routeKey: string;
      terminalId: string | null;
      terminalName: string;
      selectedCallId?: string;
      candidates: TerminalCall[];
      terminal: any;
      invalidCallIds?: Set<string>;
      rowWarning?: string | null;
    }) => {
      const hasMatches = candidates.length > 0;
      const hasMultipleMatches = candidates.length > 1;
      const selectedCall = findCallByIdMemo(candidates, selectedCallId);
      const translatedType = type === "loading" ? t("loading") : t("unloading");
      const fieldName =
        type === "loading"
          ? `loadingCalls.${routeKey}`
          : `unloadingCalls.${routeKey}`;

      return (
        <Box>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            {selectedCallId ? (
              rowWarning && type === "unloading" ? (
                <WarningAmberOutlinedIcon
                  sx={{ color: "#ef4444", fontSize: 22, mt: 0.7 }}
                />
              ) : (
                <CheckCircleOutlineIcon
                  sx={{ color: "#4caf50", fontSize: 22, mt: 0.7 }}
                />
              )
            ) : hasMatches ? (
              <InfoOutlinedIcon
                sx={{ color: "#2f80ed", fontSize: 22, mt: 0.7 }}
              />
            ) : (
              <WarningAmberOutlinedIcon
                sx={{ color: "#f97316", fontSize: 22, mt: 0.7 }}
              />
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {terminalId ? (
                <Stack spacing={0.75}>
                  {hasMatches ? (
                    candidates.map((call) => {
                      const callId = getCallId(call);
                      const invalid = Boolean(
                        invalidCallIds?.has(callId) && type === "unloading"
                      );

                      return renderCallOption({
                        call,
                        checked: callId === selectedCallId,
                        fieldName,
                        invalid,
                        invalidLabel:
                          invalid && type === "unloading"
                            ? t("unloadsBeforeOrAtLoadingCall")
                            : undefined,
                      });
                    })
                  ) : (
                    <Typography
                      sx={{
                        mt: 0.5,
                        color: "#f97316",
                        fontSize: "0.72rem",
                        fontWeight: 800,
                      }}
                    >
                      {t("noMatchingTerminalCallFound")}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Typography color="text.secondary" sx={SMALL_TEXT}>
                  {type === "loading"
                    ? t("noLoadingTerminal")
                    : t("noUnloadingTerminal")}
                </Typography>
              )}

              {hasMultipleMatches && !selectedCallId ? (
                <Alert
                  severity="warning"
                  variant="outlined"
                  icon={<WarningAmberOutlinedIcon />}
                  sx={{
                    mt: 0.85,
                    py: 0.2,
                    borderColor: "rgba(249, 115, 22, 0.35)",
                    backgroundColor: "rgba(249, 115, 22, 0.05)",
                    "& .MuiAlert-message": {
                      py: 0.35,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 800,
                      color: "#9a3412",
                    }}
                  >
                    {t("multipleTerminalCallsFoundSelectCorrect", {
                      type: translatedType.toLowerCase(),
                    })}
                  </Typography>
                </Alert>
              ) : null}

              {hasMatches && candidates.length === 1 ? (
                <Typography
                  sx={{
                    mt: 0.65,
                    color: "text.secondary",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                  }}
                >
                  {t("oneMatchingTerminalCallFoundAndSelected", {
                    type: translatedType.toLowerCase(),
                  })}
                </Typography>
              ) : null}

              {selectedCall ? (
                <Typography sx={{ ...SMALL_TEXT, mt: 0.5 }}>
                  {selectedCall.terminal?.name || terminalName}
                </Typography>
              ) : null}

              {hasMatches ? (
                <Chip
                  size="small"
                  label={t("terminalCallsFound", {
                    count: candidates.length,
                  })}
                  sx={{
                    mt: 1,
                    height: 24,
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    backgroundColor: "rgba(76, 175, 80, 0.14)",
                    color: "#2e7d32",
                  }}
                />
              ) : null}
            </Box>

            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={isSubmitting || isCancelling}
              onClick={() =>
                openCreateCallModal({
                  terminal,
                  terminalName,
                  mode: type,
                  routeKey,
                })
              }
              sx={{
                minWidth: 92,
                height: 36,
                textTransform: "none",
                fontSize: "0.76rem",
                fontWeight: 800,
                borderColor: "#8ab9f1",
                color: "#1976d2",
              }}
            >
              {t("addNew")}
            </Button>
          </Stack>
        </Box>
      );
    },
    [
      findCallByIdMemo,
      openCreateCallModal,
      renderCallOption,
      isSubmitting,
      isCancelling,
      t,
    ]
  );

  const renderRouteTable = useCallback(
    (routes: any[]) => {
      if (!routes.length) {
        return (
          <Typography sx={SMALL_TEXT}>
            {t("noContainerRoutesFoundForSelectedContainers")}
          </Typography>
        );
      }

      return (
        <Box sx={{ width: "100%" }}>
          <Alert
            severity="info"
            variant="outlined"
            icon={<InfoOutlinedIcon />}
            sx={{
              mb: 2,
              borderColor: "#bdd7ff",
              backgroundColor: "rgba(47, 128, 237, 0.04)",
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 800 }}>
              {t("linkEachRouteToExistingTerminalCall")}
            </Typography>

            <Typography sx={{ fontSize: "0.76rem", color: "text.secondary" }}>
              {t("selectCorrectTerminalCallDescription")}
            </Typography>
          </Alert>

          <Table
            size="small"
            sx={{
              width: "100%",
              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: "#eaf3ff",
                fontSize: "0.76rem",
                fontWeight: 900,
                borderBottom: "none",
                py: 1.2,
              },
              "& .MuiTableBody-root .MuiTableCell-root": {
                fontSize: "0.76rem",
                borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                py: 1.4,
                verticalAlign: "top",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "26%" }}>{t("routeFromFile")}</TableCell>
                <TableCell sx={{ width: "37%" }}>
                  {t("loadingTerminalCall")}
                </TableCell>
                <TableCell sx={{ width: "37%" }}>
                  {t("unloadingTerminalCall")}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {routes.map((group, index) => {
                const loadingTermId: string | null = group.loadingTerminalId;
                const unloadingTermId: string | null = group.unloadingTerminalId;

                const loadingSelected = watchedLoading[group.key] || "";
                const unloadingSelected = watchedUnloading[group.key] || "";

                const loadingCandidates = getMatchesForTerminal(
                  loadingCalls,
                  loadingTermId
                );

                const unloadingCandidates = getMatchesForTerminal(
                  unloadingCalls,
                  unloadingTermId
                );

                const selectedLoadingCall = findCallByIdMemo(
                  loadingCandidates,
                  loadingSelected || undefined
                );

                const selectedUnloadingCall = findCallByIdMemo(
                  unloadingCandidates,
                  unloadingSelected || undefined
                );

                const rowWarning = getSelectedCallWarning(
                  selectedLoadingCall,
                  selectedUnloadingCall,
                  t
                );

                const invalidUnloadingCallIds = new Set<string>();

                unloadingCandidates.forEach((call) => {
                  const warning = getSelectedCallWarning(
                    selectedLoadingCall,
                    call,
                    t
                  );

                  if (warning && call["@id"]) {
                    invalidUnloadingCallIds.add(call["@id"]);
                  }
                });

                const statusMatched = Boolean(
                  loadingSelected && unloadingSelected && !rowWarning
                );

                return (
                  <TableRow
                    key={group.key}
                    sx={{
                      backgroundColor: rowWarning
                        ? "rgba(239, 68, 68, 0.035)"
                        : "transparent",
                      "& .MuiTableCell-root": {
                        borderBottomColor: rowWarning
                          ? "rgba(239, 68, 68, 0.2)"
                          : "rgba(15, 23, 42, 0.08)",
                      },
                    }}
                  >
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={1.2}
                        alignItems="flex-start"
                      >
                        {statusMatched ? (
                          <CheckCircleOutlineIcon
                            sx={{ color: "#4caf50", fontSize: 22, mt: 0.2 }}
                          />
                        ) : (
                          <WarningAmberOutlinedIcon
                            sx={{
                              color: rowWarning ? "#ef4444" : "#f97316",
                              fontSize: 22,
                              mt: 0.2,
                            }}
                          />
                        )}

                        <Chip
                          label={index + 1}
                          size="small"
                          variant="outlined"
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.72rem",
                            fontWeight: 900,
                            borderRadius: "50%",
                            borderColor: rowWarning
                              ? "rgba(239, 68, 68, 0.5)"
                              : undefined,
                            color: rowWarning ? "#b91c1c" : undefined,
                          }}
                        />

                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 900,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              color: rowWarning ? "#b91c1c" : "text.primary",
                            }}
                          >
                            {group.loadingName}
                            <ArrowRightAltIcon
                              sx={{
                                fontSize: 17,
                                color: rowWarning ? "#ef4444" : "#1976d2",
                              }}
                            />
                            {group.unloadingName}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.35,
                              fontSize: "0.72rem",
                              color: "text.secondary",
                              fontWeight: 700,
                            }}
                          >
                            {t("containers")}: {group.containers.length}
                            {"   |   "}
                            {t("weight")}: {formatWeight(group.totalWeight)}
                          </Typography>

                          {rowWarning ? (
                            <Alert
                              severity="error"
                              variant="outlined"
                              icon={<ErrorOutlineIcon />}
                              sx={{
                                mt: 1,
                                py: 0.2,
                                borderColor: "rgba(239, 68, 68, 0.4)",
                                backgroundColor: "rgba(239, 68, 68, 0.06)",
                                "& .MuiAlert-message": {
                                  py: 0.35,
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.68rem",
                                  fontWeight: 800,
                                  color: "#b91c1c",
                                }}
                              >
                                {rowWarning}
                              </Typography>
                            </Alert>
                          ) : null}
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      {renderCallPicker({
                        type: "loading",
                        routeKey: group.key,
                        terminalId: loadingTermId,
                        terminalName: group.loadingName,
                        selectedCallId: loadingSelected || undefined,
                        candidates: loadingCandidates,
                        terminal: group.containers[0]?.loadingTerminal ?? null,
                        rowWarning,
                      })}
                    </TableCell>

                    <TableCell>
                      {renderCallPicker({
                        type: "unloading",
                        routeKey: group.key,
                        terminalId: unloadingTermId,
                        terminalName: group.unloadingName,
                        selectedCallId: unloadingSelected || undefined,
                        candidates: unloadingCandidates,
                        terminal: group.containers[0]?.unloadingTerminal ?? null,
                        invalidCallIds: invalidUnloadingCallIds,
                        rowWarning,
                      })}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      );
    },
    [
      watchedLoading,
      watchedUnloading,
      loadingCalls,
      unloadingCalls,
      getMatchesForTerminal,
      findCallByIdMemo,
      renderCallPicker,
      t,
    ]
  );

  const onSubmit: SubmitHandler<FormValues> = async (formData) => {
    if (!allRowsHaveSelectedTerminalCalls) {
      window.alert(t("selectLoadingAndUnloadingCallsForEveryRoute"));
      return;
    }

    const routeAssignments = routeGroups.map((route) => {
      const loadingCallId = formData.loadingCalls[route.key];
      const unloadingCallId = formData.unloadingCalls[route.key];

      return {
        routeKey: route.key,
        loadingTerminal: route.loadingTerminalId,
        unloadingTerminal: route.unloadingTerminalId,
        loadingTerminalCall: loadingCallId || null,
        unloadingTerminalCall: unloadingCallId || null,
        containerIds: route.containers.map(getContainerApiId).filter(Boolean),
      };
    });

    let result: any = null;

    try {
      const response = await fetch("/api/container-terminal-call", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          containerIds,
          routeAssignments,
          loadingCalls: formData.loadingCalls,
          unloadingCalls: formData.unloadingCalls,
        }),
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || `Server error: ${response.statusText}`);
      }

      result = responseText ? JSON.parse(responseText) : null;

      hasSubmittedRef.current = true;

      await refreshParentPage({
        ...(result || {}),
        voyage,
        containerIds,
        containerUploadId,
      });

      closeWithoutCleanup();

      if (voyage) {
        refreshVoyageData(voyage);
      }
    } catch (error) {
      console.error("Save failed", error);
      window.alert(t("failedToLinkContainersToTerminalCalls"));
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      position="relative"
      sx={{
        width: "100%",
        maxWidth: "none",
        boxSizing: "border-box",
        backgroundColor: "#fff",
      }}
    >
      {(containersLoading ||
        terminalCallsLoading ||
        isSubmitting ||
        isCancelling) && (
        <Box
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.65)"
          zIndex={999}
          width="100%"
          height="100%"
        >
          <LinearProgress sx={{ width: "40%" }} />
        </Box>
      )}

      <Box sx={{ px: 2, pt: 1.5, pb: 1.5 }}>
        <Typography sx={{ fontSize: "1rem", fontWeight: 900 }}>
          {t("linkContainersToTerminalCalls")}
        </Typography>

        <Typography
          sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.35 }}
        >
          {t("linkContainersToTerminalCallsDescription")}
        </Typography>
      </Box>

      <Divider />

      <Box
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: "none",
          minWidth: 0,
          boxSizing: "border-box",
          minHeight: "72vh",
          maxHeight: "72vh",
        }}
      >
        <Box
          sx={{
            flex: "0 0 300px",
            minWidth: 300,
            overflowY: "auto",
            borderRight: "1px solid rgba(15, 23, 42, 0.1)",
            p: 1.25,
            backgroundColor: "#fbfdff",
            "&::-webkit-scrollbar": { width: 0 },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <Typography sx={{ fontSize: "0.86rem", fontWeight: 900, mb: 1 }}>
            {t("routesInFile", { count: routeGroups.length })}
          </Typography>

          <Stack spacing={1}>
            {groupedByRoute.map((route, index) => (
              <Accordion
                key={route.key}
                defaultExpanded={index === 0}
                disableGutters
                sx={{
                  border: "1px solid rgba(47, 128, 237, 0.22)",
                  borderRadius: "8px !important",
                  boxShadow: "none",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  "&::before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    minHeight: 42,
                    px: 1,
                    backgroundColor:
                      index === 0 ? "rgba(47, 128, 237, 0.08)" : "#fff",
                    "& .MuiAccordionSummary-content": {
                      my: 0.75,
                      minWidth: 0,
                    },
                  }}
                >
                  <Box sx={{ width: "100%", minWidth: 0 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      sx={{ minWidth: 0 }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 900,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.55,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        {route.loadingName}
                        <ArrowRightAltIcon
                          sx={{
                            fontSize: 15,
                            color: "#1976d2",
                            flexShrink: 0,
                          }}
                        />
                        {route.unloadingName}
                      </Typography>

                      <Chip
                        size="small"
                        label={`${route.containers.length}`}
                        sx={{
                          height: 19,
                          minWidth: 28,
                          fontSize: "0.62rem",
                          fontWeight: 900,
                          backgroundColor: "#eef4ff",
                          flexShrink: 0,
                        }}
                      />
                    </Stack>

                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: "0.66rem",
                        color: "text.secondary",
                        fontWeight: 700,
                      }}
                    >
                      {formatWeight(route.totalWeight)}
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  <Box
                    sx={{
                      maxHeight: 365,
                      overflow: "auto",
                      "&::-webkit-scrollbar": { width: 4 },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(15, 23, 42, 0.2)",
                        borderRadius: 999,
                      },
                    }}
                  >
                    <Table
                      size="small"
                      stickyHeader
                      sx={{
                        tableLayout: "fixed",
                        "& .MuiTableCell-root": {
                          borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
                          fontSize: "0.68rem",
                          py: 0.65,
                          px: 0.75,
                          whiteSpace: "nowrap",
                        },
                        "& .MuiTableHead-root .MuiTableCell-root": {
                          backgroundColor: "#f8fbff",
                          color: "text.secondary",
                          fontSize: "0.62rem",
                          fontWeight: 900,
                          textTransform: "uppercase",
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 30 }}>#</TableCell>
                          <TableCell sx={{ width: 118 }}>
                            {t("container")}
                          </TableCell>
                          <TableCell sx={{ width: 58 }} align="center">
                            {t("type")}
                          </TableCell>
                          <TableCell sx={{ width: 74 }} align="right">
                            {t("weight")}
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {route.previewContainers.map(
                          (container: any, idx: number) => (
                            <TableRow
                              key={container["@id"] || container.uid || idx}
                              hover
                            >
                              <TableCell sx={{ color: "text.secondary" }}>
                                {idx + 1}
                              </TableCell>

                              <TableCell>
                                <Typography
                                  noWrap
                                  title={container.uid || "—"}
                                  sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 900,
                                  }}
                                >
                                  {container.uid || "—"}
                                </Typography>
                              </TableCell>

                              <TableCell align="center">
                                <Typography
                                  noWrap
                                  title={getContainerTypeLabel(container)}
                                  sx={{
                                    fontSize: "0.68rem",
                                    color: "text.secondary",
                                    fontWeight: 800,
                                  }}
                                >
                                  {getContainerTypeLabel(container)}
                                </Typography>
                              </TableCell>

                              <TableCell align="right">
                                <Typography
                                  noWrap
                                  sx={{
                                    fontSize: "0.68rem",
                                    color: "text.secondary",
                                    fontWeight: 800,
                                  }}
                                >
                                  {formatWeight(getContainerWeight(container))}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </Box>

                  {route.remainingCount > 0 ? (
                    <Typography
                      sx={{
                        px: 1.25,
                        py: 0.85,
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        fontWeight: 800,
                        borderTop: "1px solid rgba(15, 23, 42, 0.06)",
                        backgroundColor: "#fff",
                      }}
                    >
                      {t("andMore", { count: route.remainingCount })}
                    </Typography>
                  ) : null}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 1.25,
              borderRadius: 1.5,
              backgroundColor: "#fff",
            }}
          >
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Inventory2OutlinedIcon
                    sx={{ fontSize: 17, color: "text.secondary" }}
                  />
                  <Typography sx={SMALL_TEXT}>{t("totalContainers")}</Typography>
                </Stack>

                <Typography sx={SMALL_BOLD_TEXT}>{totalContainers}</Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <ScaleOutlinedIcon
                    sx={{ fontSize: 17, color: "text.secondary" }}
                  />
                  <Typography sx={SMALL_TEXT}>{t("totalWeight")}</Typography>
                </Stack>

                <Typography sx={SMALL_BOLD_TEXT}>
                  {formatWeight(totalWeight)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Box>

        <Box
          sx={{
            flex: "1 1 auto",
            minWidth: 0,
            width: "100%",
            overflow: "auto",
            p: 2,
            "&::-webkit-scrollbar": { width: 0 },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {renderRouteTable(routeGroups)}
        </Box>
      </Box>

      <Divider />

      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ p: 1.5 }}
      >
        <Button
          onClick={close}
          startIcon={<CloseIcon />}
          variant="outlined"
          disabled={isSubmitting || isCancelling}
          sx={{
            textTransform: "none",
            fontSize: "0.78rem",
            fontWeight: 800,
          }}
        >
          {isCancelling ? t("cancellingImport") : t("cancel")}
        </Button>

        <Stack direction="row" spacing={1} alignItems="center">
          {!allRowsHaveSelectedTerminalCalls ? (
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 800,
                color: "#f97316",
              }}
            >
              {t("selectLoadingAndUnloadingCallsForEveryRoute")}
            </Typography>
          ) : null}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            endIcon={<ArrowRightAltIcon />}
            startIcon={<AddLinkIcon />}
            disabled={linkAllDisabled}
            sx={{
              textTransform: "none",
              fontSize: "0.78rem",
              fontWeight: 900,
              px: 2.5,
            }}
          >
            {t("linkAllContinue")}
          </Button>
        </Stack>
      </Grid>
    </Box>
  );
};

export default ContainerTerminalCallForm;